import type { MillConstants } from "@/lib/mill/constants";
import { DEFAULT_MILL_CONSTANTS } from "@/lib/mill/constants";

export function stripWidthFromOd(
  od: number,
  thickness: number,
  weldAllowance = DEFAULT_MILL_CONSTANTS.weld_allowance_mm,
): number {
  return Math.PI * (od - thickness) + weldAllowance;
}

export type StripDemand = {
  stripWidth: number;
  count: number;
  shape_size_id?: string | null;
  label?: string;
  isFiller?: boolean;
};

export type NestPattern = {
  strips: StripDemand[];
  usedWidth: number;
  leftover: number;
  scrapPct: number;
  fillerCount: number;
  orderedStripCount: number;
};

function packCost(strips: StripDemand[], kerf: number): number {
  const widths: number[] = [];
  for (const s of strips) {
    for (let i = 0; i < s.count; i++) widths.push(s.stripWidth);
  }
  if (!widths.length) return 0;
  return widths.reduce((a, b) => a + b, 0) + kerf * (widths.length - 1);
}

/** Greedy nest: largest strips first into usable coil width. */
export function nestStrips(input: {
  coilWidth: number;
  demands: StripDemand[];
  fillers?: StripDemand[];
  constants?: Partial<MillConstants>;
  leftoverThreshold?: number;
}): NestPattern[] {
  const c = { ...DEFAULT_MILL_CONSTANTS, ...input.constants };
  const usable = input.coilWidth - 2 * c.edge_trim_mm;
  if (usable <= 0) return [];

  const base = [...input.demands]
    .filter((d) => d.stripWidth > 0 && d.count > 0)
    .sort((a, b) => b.stripWidth - a.stripWidth);

  const patterns: NestPattern[] = [];

  const tryPack = (strips: StripDemand[]): NestPattern | null => {
    const cost = packCost(strips, c.kerf_mm);
    if (cost > usable + 1e-6) return null;
    const leftover = usable - cost;
    const orderedStripCount = strips
      .filter((s) => !s.isFiller)
      .reduce((n, s) => n + s.count, 0);
    const fillerCount = strips
      .filter((s) => s.isFiller)
      .reduce((n, s) => n + s.count, 0);
    return {
      strips,
      usedWidth: cost,
      leftover,
      scrapPct: usable > 0 ? (leftover / usable) * 100 : 0,
      fillerCount,
      orderedStripCount,
    };
  };

  // Greedy: take as many of each demand as fit
  const greedy: StripDemand[] = [];
  for (const d of base) {
    let taken = 0;
    for (let i = 0; i < d.count; i++) {
      const flat: StripDemand[] = [
        ...greedy.filter((g) => g.count > 0),
        { ...d, count: taken + 1 },
      ];
      const cost = packCost(flat, c.kerf_mm);
      if (cost <= usable + 1e-6) {
        taken += 1;
      } else break;
    }
    if (taken > 0) greedy.push({ ...d, count: taken });
  }

  const basePattern = tryPack(greedy);
  if (basePattern) patterns.push(basePattern);

  const threshold = input.leftoverThreshold ?? 40;
  const fillers = (input.fillers ?? []).filter((f) => f.stripWidth > 0);

  if (basePattern && basePattern.leftover >= threshold && fillers.length) {
    for (const f of fillers.slice(0, 12)) {
      for (let n = 1; n <= 2; n++) {
        const withFiller = [
          ...greedy,
          { ...f, count: n, isFiller: true },
        ];
        const p = tryPack(withFiller);
        if (p && p.leftover < basePattern.leftover) patterns.push(p);
      }
    }
  }

  // Two-filler combo (limited)
  if (basePattern && basePattern.leftover >= threshold && fillers.length >= 2) {
    const a = fillers[0];
    const b = fillers[1];
    const with2 = [
      ...greedy,
      { ...a, count: 1, isFiller: true },
      { ...b, count: 1, isFiller: true },
    ];
    const p = tryPack(with2);
    if (p && p.leftover < basePattern.leftover) patterns.push(p);
  }

  return patterns.sort((x, y) => {
    if (x.leftover !== y.leftover) return x.leftover - y.leftover;
    if (x.fillerCount !== y.fillerCount) return x.fillerCount - y.fillerCount;
    return y.orderedStripCount - x.orderedStripCount;
  });
}
